import React, { useState } from 'react';
import { Camera, Edit2, X, Check, Loader2 } from 'lucide-react';
import { ref, update } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../../../../integrations/firebase';

const UserProfileEditor = ({ currentUser, isOpen, onClose }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [newName, setNewName] = useState(currentUser?.name || '');
    const [uploading, setUploading] = useState(false);

    if (!currentUser || !isOpen) return null;

    const handleUpdateName = async () => {
        if (!newName.trim()) return;
        try {
            await update(ref(db, `users/${currentUser.uid}`), {
                name: newName
            });
            setIsEditing(false);
        } catch (error) {
            console.error("Error updating name:", error);
            alert("Failed to update name");
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const storage = getStorage();
        const fileRef = storageRef(storage, `profiles/${currentUser.uid}_${Date.now()}`);

        try {
            await uploadBytes(fileRef, file);
            const photoURL = await getDownloadURL(fileRef);

            await update(ref(db, `users/${currentUser.uid}`), {
                photo: photoURL
            });
        } catch (error) {
            console.error("Error uploading photo:", error);
            alert("Failed to upload photo");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl scale-100 animate-in zoom-in-95 duration-200 mx-4">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-slate-800">Edit Profile</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 bg-slate-100 rounded-full">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex flex-col items-center gap-6">
                    {/* Photo Upload */}
                    <div className="relative group">
                        <img
                            src={currentUser.photo || `https://ui-avatars.com/api/?name=${currentUser.name || 'User'}&background=4F46E5&color=fff`}
                            className="w-24 h-24 rounded-full object-cover border-4 border-slate-100 shadow-md"
                            alt="Profile"
                        />
                        <label className="absolute bottom-0 right-0 bg-indigo-600 text-white p-2 rounded-full cursor-pointer shadow-lg hover:bg-indigo-700 transition-colors">
                            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                            <input type="file" className="hidden" accept="image/*" onChange={handlePhotoUpload} disabled={uploading} />
                        </label>
                    </div>

                    {/* Name Edit */}
                    <div className="w-full">
                        <label className="block text-sm font-medium text-slate-500 mb-1">Display Name</label>
                        <div className="flex gap-2">
                            {isEditing ? (
                                <>
                                    <input
                                        type="text"
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        className="flex-1 border border-indigo-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                                        autoFocus
                                    />
                                    <button
                                        onClick={handleUpdateName}
                                        className="bg-indigo-600 text-white p-2 rounded-lg hover:bg-indigo-700"
                                    >
                                        <Check size={20} />
                                    </button>
                                    <button
                                        onClick={() => setIsEditing(false)}
                                        className="bg-slate-100 text-slate-600 p-2 rounded-lg hover:bg-slate-200"
                                    >
                                        <X size={20} />
                                    </button>
                                </>
                            ) : (
                                <div className="flex-1 flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                                    <span className="font-medium text-slate-900">{currentUser.name || 'Set a name'}</span>
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="text-indigo-600 hover:text-indigo-700 p-1"
                                    >
                                        <Edit2 size={16} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="w-full bg-indigo-50 p-4 rounded-xl text-xs text-indigo-700 leading-relaxed">
                        <p><strong>Note:</strong> Changes to your profile will be visible to all team members instantly.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserProfileEditor;
